import type { Feed } from '~/services/layout/index.ts';
import { getParamInsensitive } from '~/services/layout/layout';

/**
 * Get the group info title, potentially overridden by query parameters in the feed path.
 * This function checks for the presence of 'overrideFeedTitle' and 'feedTitle' query
 * parameters in the feed's _feedPath URL.
 * If neither is present, it falls back to the localization override or feed's title.
 * Since the feed title override is handled in the DSP side, if isClientStorage is false
 * or _feedPath is not present, it returns the localization title override or the feed's
 * title without checking query parameters.
 * @param feed - The feed object.
 * @param isClientStorage - Flag indicating if the feed is from client storage.
 * @param localizations - An object containing localized strings.
 * @returns The overridden title if specified in the query parameters, otherwise the localization title
 * override or feed's title.
 */
export function getGroupInfoTitle({
  feed,
  localizations,
  isClientStorage,
}: {
  feed?: Feed | null;
  localizations?: { title_override?: string };
  isClientStorage?: boolean;
}): string | undefined {
  const title = localizations?.title_override || feed?.title;

  if (!isClientStorage || !feed?._feedPath) return title;

  try {
    const url = new URL(feed?._feedPath);

    const feedTitleParamValue = getParamInsensitive(url, 'feedTitle');

    const overrideFeedTitleParamValue = getParamInsensitive(
      url,
      'overrideFeedTitle'
    );

    return feedTitleParamValue || overrideFeedTitleParamValue || title;
  } catch (error) {
    return title;
  }
}
